import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Image, ListGroup, Badge } from 'react-bootstrap';
import { UserContext } from '../Contexts/UserContext';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaEye, FaUser, FaGlobe, FaUsers, FaCrown, FaEdit, FaCalendarAlt, FaFingerprint } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const UserProfile = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [qpAdmRuns, setQpAdmRuns] = useState();

    const getqpAdmRuns = useCallback(async (token) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}qpadm_runs/user/${token}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user?.token}`,
            },
        });

        if (!response.ok) {
            console.log('Network response was not ok');
        }

        return response.json()
    }, [user?.token]);

    useEffect(() => {
        if (user?.token) {
            getqpAdmRuns(user?.uuid).then((res) => {
                if (res) {
                    setQpAdmRuns(res);
                }
            });
        }
    }, [user, getqpAdmRuns]); // Runs the effect when the 'user' changes

    useEffect(() => {
        // Check if user is populated and has a token
        if (!user?.token) {
            // Perform the redirect or other action if token is missing
            navigate('/login_required');
        }
    }, [user, navigate]);

    const styles = {
        pageContainer: {
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            padding: '2rem 0',
        },
        profileCard: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: '#ffffff',
        },
        profileHeader: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            padding: '2rem',
            color: 'white',
            textAlign: 'center',
        },
        profileImage: {
            width: '150px',
            height: '150px',
            border: '4px solid white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            marginBottom: '1rem',
        },
        profileName: {
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
        },
        profileRole: {
            fontSize: '1.1rem',
            opacity: '0.9',
            marginBottom: '1rem',
        },
        listGroup: {
            padding: '1.5rem',
        },
        listItem: {
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            background: '#f8fafc',
            border: 'none',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            '&:hover': {
                background: '#edf2f7',
            },
        },
        editButton: {
            background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: '600',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
            },
        },
        runsCard: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: '#ffffff',
            height: '100%',
        },
        runsHeader: {
            padding: '1.5rem',
            borderBottom: '1px solid #e2e8f0',
        },
        runItem: {
            padding: '1.25rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            background: '#f8fafc',
            border: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#edf2f7',
                transform: 'translateY(-2px)',
            },
        },
        runInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            color: '#4a5568',
        },
        statusBadge: {
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500',
            fontSize: '0.9rem',
        },
        viewButton: {
            color: '#2C5282',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#1a365d',
            },
        },
        icon: {
            fontSize: '1.2rem',
            marginRight: '0.5rem',
        },
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | User Profile</title>
                <meta name="description" content="View and manage your DNACloudHub account settings, track your usage, and access your genetic analysis projects on our cloud-based platform for running qpAdm models." />
                <meta property="og:title" content={'DNACloudHub | User Profile'} />
                <meta property="og:description" content={'"View and manage your DNACloudHub account settings, track your usage, and access your genetic analysis projects on our cloud-based platform for running qpAdm models."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/user_profile`} />
            </Helmet>

            <div style={styles.pageContainer}>
                <Container>
                    <Row className="g-4">
                        {/* User Information Section */}
                        <Col md={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <Card style={styles.profileCard}>
                                    <div style={styles.profileHeader}>
                                        <Image
                                            src="/user_profile.jpg"
                                            alt="User Profile"
                                            roundedCircle
                                            style={styles.profileImage}
                                        />
                                        <h4 style={styles.profileName}>{user?.first_name} {user?.last_name}</h4>
                                        <p style={styles.profileRole}>
                                            <Badge bg="light" text="dark" pill style={styles.statusBadge}>
                                                {user?.role}
                                            </Badge>
                                        </p>
                                    </div>
                                    <div style={styles.listGroup}>
                                        <ListGroup variant="flush">
                                            <motion.div
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: 0.2 }}
                                            >
                                                <ListGroup.Item style={styles.listItem}>
                                                    <FaUser style={styles.icon} />
                                                    <div>
                                                        <small className="text-muted">Username</small>
                                                        <div><strong>{user?.username}</strong></div>
                                                    </div>
                                                </ListGroup.Item>
                                            </motion.div>
                                            <motion.div
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: 0.3 }}
                                            >
                                                <ListGroup.Item style={styles.listItem}>
                                                    <FaGlobe style={styles.icon} />
                                                    <div>
                                                        <small className="text-muted">Country</small>
                                                        <div><strong>{user?.country}</strong></div>
                                                    </div>
                                                </ListGroup.Item>
                                            </motion.div>
                                            <motion.div
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: 0.4 }}
                                            >
                                                <ListGroup.Item style={styles.listItem}>
                                                    <FaUsers style={styles.icon} />
                                                    <div>
                                                        <small className="text-muted">Ethnicity</small>
                                                        <div><strong>{user?.ethnicity}</strong></div>
                                                    </div>
                                                </ListGroup.Item>
                                            </motion.div>
                                            <motion.div
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: 0.5 }}
                                            >
                                                <ListGroup.Item style={styles.listItem}>
                                                    <FaCrown style={styles.icon} />
                                                    <div>
                                                        <small className="text-muted">Subscription</small>
                                                        <div><strong>{user?.subscription_name}</strong></div>
                                                    </div>
                                                </ListGroup.Item>
                                            </motion.div>
                                        </ListGroup>
                                        <Link to={`/user_profile/edit`}>
                                            <Button variant="primary" className="w-100 mt-4" style={styles.editButton}>
                                                <FaEdit style={styles.icon} /> Edit Profile
                                            </Button>
                                        </Link>
                                    </div>
                                </Card>
                            </motion.div>
                        </Col>

                        {/* qpAdm Runs Section */}
                        <Col md={8}>
                            <Row className="g-4">
                                {/* Static Runs Card */}
                                <Col md={12}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                    >
                                        <Card style={styles.runsCard}>
                                            <div style={{
                                                ...styles.runsHeader,
                                                background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
                                                color: 'white'
                                            }}>
                                                <h5 className="mb-0">Static Runs</h5>
                                            </div>
                                            <Card.Body>
                                                <ListGroup variant="flush">
                                                    {qpAdmRuns?.filter(run => run.run_type === 'static').map((run, index) => (
                                                        <motion.div
                                                            key={run?.id}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                                        >
                                                            <ListGroup.Item style={styles.runItem}>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <div style={styles.runInfo}>
                                                                            <FaFingerprint />
                                                                            <strong>{run?.uuid?.toUpperCase()}</strong>
                                                                        </div>
                                                                        <div style={styles.runInfo} className="mt-2">
                                                                            <FaCalendarAlt />
                                                                            {new Date(run.created_at).toDateString()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <Badge
                                                                            bg={run.success ? "success" : "danger"}
                                                                            style={styles.statusBadge}
                                                                        >
                                                                            {run.success ? (
                                                                                <><FaCheckCircle /> SUCCESS</>
                                                                            ) : (
                                                                                <><FaTimesCircle /> FAILED</>
                                                                            )}
                                                                        </Badge>
                                                                        <Link
                                                                            to={`/qpadm_runs/${run.uuid}`}
                                                                            style={styles.viewButton}
                                                                        >
                                                                            <FaEye /> View
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </motion.div>
                                                    ))}
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>

                                {/* Rotation Runs Card */}
                                <Col md={12}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.4 }}
                                    >
                                        <Card style={styles.runsCard}>
                                            <div style={{
                                                ...styles.runsHeader,
                                                background: 'linear-gradient(135deg, #276749 0%, #48BB78 100%)',
                                                color: 'white'
                                            }}>
                                                <h5 className="mb-0">Rotation Runs</h5>
                                            </div>
                                            <Card.Body>
                                                <ListGroup variant="flush">
                                                    {qpAdmRuns?.filter(run => run.run_type === 'rotation').map((run, index) => (
                                                        <motion.div
                                                            key={run?.id}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                                        >
                                                            <ListGroup.Item style={styles.runItem}>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <div style={styles.runInfo}>
                                                                            <FaFingerprint />
                                                                            <strong>{run.uuid.toUpperCase()}</strong>
                                                                        </div>
                                                                        <div style={styles.runInfo} className="mt-2">
                                                                            <FaCalendarAlt />
                                                                            {new Date(run.created_at).toDateString()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <Badge
                                                                            bg={run.success ? "success" : "danger"}
                                                                            style={styles.statusBadge}
                                                                        >
                                                                            {run.success ? (
                                                                                <><FaCheckCircle /> SUCCESS</>
                                                                            ) : (
                                                                                <><FaTimesCircle /> FAILED</>
                                                                            )}
                                                                        </Badge>
                                                                        <Link
                                                                            to={`/qpadm_runs/${run.uuid}`}
                                                                            style={styles.viewButton}
                                                                        >
                                                                            <FaEye /> View
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </motion.div>
                                                    ))}
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default UserProfile;
